import { useCallback, useEffect, useMemo, useState } from 'react';

import { reportsAudit, type AuditItem } from '../../lib/panelApi';

type Props = {
  accessToken: string;
};

const PAGE_SIZE = 50;

export function AdminActivityPage({ accessToken }: Props) {
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [items, setItems] = useState<AuditItem[]>([]);
  const [nextOffset, setNextOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [actionFilter, setActionFilter] = useState('');
  const [adminEmailFilter, setAdminEmailFilter] = useState('');
  const [ipFilter, setIPFilter] = useState('');
  const [targetKindFilter, setTargetKindFilter] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [q, setQ] = useState('');

  const queryParams = useMemo(
    () => ({
      action: actionFilter.trim(),
      admin_email: adminEmailFilter.trim(),
      ip: ipFilter.trim(),
      target_kind: targetKindFilter.trim(),
      from: fromDate.trim(),
      to: toDate.trim(),
      q: q.trim(),
    }),
    [actionFilter, adminEmailFilter, ipFilter, targetKindFilter, fromDate, toDate, q],
  );

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await reportsAudit(accessToken, { ...queryParams, limit: PAGE_SIZE, offset: 0 });
      const fresh = res.items ?? [];
      setItems(fresh);
      setNextOffset(fresh.length);
      setHasMore(fresh.length === PAGE_SIZE);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load activity logs');
    } finally {
      setLoading(false);
    }
  }, [accessToken, queryParams]);

  const loadMore = useCallback(async () => {
    if (!hasMore) return;
    setLoadingMore(true);
    setError(null);
    try {
      const res = await reportsAudit(accessToken, { ...queryParams, limit: PAGE_SIZE, offset: nextOffset });
      const more = res.items ?? [];
      setItems((prev) => {
        const merged = [...prev, ...more];
        const seen = new Set<string>();
        const deduped: AuditItem[] = [];
        for (const row of merged) {
          const id = String(row.uuid ?? '');
          if (!id || seen.has(id)) continue;
          seen.add(id);
          deduped.push(row);
        }
        return deduped;
      });
      setNextOffset((v) => v + more.length);
      if (more.length < PAGE_SIZE) setHasMore(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load more logs');
    } finally {
      setLoadingMore(false);
    }
  }, [accessToken, queryParams, nextOffset, hasMore]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const knownActions = useMemo(() => {
    const set = new Set<string>();
    for (const row of items) {
      const v = String(row.action ?? '').trim();
      if (v) set.add(v);
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [items]);

  return (
    <div className="card">
      <div className="pageHeader" style={{ marginBottom: 10 }}>
        <div>
          <div className="pageTitle">Admin Activity Logs</div>
          <div className="pageSubtitle">Track who changed what, when, and from which IP.</div>
        </div>
        <div className="toolbarGroup">
          <button className="btn" onClick={refresh} disabled={loading}>
            {loading ? 'Loading…' : 'Refresh'}
          </button>
        </div>
      </div>

      <div className="toolbar" style={{ marginBottom: 12 }}>
        <div className="toolbarGroup" style={{ flex: 1, justifyContent: 'flex-end', alignItems: 'flex-end' }}>
          <div className="field" style={{ marginTop: 0, minWidth: 260, flex: 1, maxWidth: 420 }}>
            <div className="label">Search (backend)</div>
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="email, action, target, reason, ip..." />
          </div>
          <div className="field" style={{ marginTop: 0, minWidth: 220 }}>
            <div className="label">Action</div>
            <input
              list="admin-actions"
              value={actionFilter}
              onChange={(e) => setActionFilter(e.target.value)}
              placeholder="e.g. books.update"
            />
            <datalist id="admin-actions">
              {knownActions.map((action) => (
                <option value={action} key={action} />
              ))}
            </datalist>
          </div>
          <div className="field" style={{ marginTop: 0, minWidth: 200 }}>
            <div className="label">Admin email</div>
            <input value={adminEmailFilter} onChange={(e) => setAdminEmailFilter(e.target.value)} placeholder="admin@..." />
          </div>
          <div className="field" style={{ marginTop: 0, minWidth: 160 }}>
            <div className="label">IP</div>
            <input value={ipFilter} onChange={(e) => setIPFilter(e.target.value)} placeholder="46.101..." />
          </div>
          <div className="field" style={{ marginTop: 0, minWidth: 170 }}>
            <div className="label">Target kind</div>
            <input value={targetKindFilter} onChange={(e) => setTargetKindFilter(e.target.value)} placeholder="books | users | ..." />
          </div>
          <div className="field" style={{ marginTop: 0, minWidth: 150 }}>
            <div className="label">From</div>
            <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
          </div>
          <div className="field" style={{ marginTop: 0, minWidth: 150 }}>
            <div className="label">To</div>
            <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
          </div>
        </div>
      </div>

      {error ? (
        <div style={{ marginBottom: 10 }}>
          <div className="errorBox">{error}</div>
        </div>
      ) : null}

      {loading ? (
        <div className="muted">Loading admin activity…</div>
      ) : items.length === 0 ? (
        <div className="muted">No activity logs found.</div>
      ) : (
        <div className="list">
          {items.map((row) => (
            <div className="item" key={row.uuid}>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                <span className="pill" style={{ fontWeight: 900 }}>
                  {row.action || 'unknown.action'}
                </span>
                <span style={{ fontWeight: 800 }}>{row.admin_email || 'unknown admin'}</span>
              </div>
              <div className="muted" style={{ marginTop: 6, fontSize: 12 }}>
                {(row.target_kind || 'target')} {row.target_uuid ? row.target_uuid : ''}
                {row.reason ? ` • ${row.reason}` : ''}
                {row.ip ? ` • ${row.ip}` : ''}
                {' • '}
                {new Date(row.created_at).toLocaleString()}
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="row" style={{ justifyContent: 'space-between', marginTop: 12 }}>
        <span className="pill">
          <span className="muted">Loaded</span>
          <span>{items.length}</span>
        </span>
        <button className="btn" onClick={loadMore} disabled={loading || loadingMore || !hasMore}>
          {loadingMore ? 'Loading…' : hasMore ? `Load more (${PAGE_SIZE})` : 'No more logs'}
        </button>
      </div>
    </div>
  );
}

